import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { fetchPatient } from "../../store/slices/patientsSlice";
import { FAMILY_SECTIONS, FAMILY_LIKERT_LEGEND } from "./familyFeedbackConfig";

const FamilyFeedbackForm = ({ formData, setFormData, readOnly = false }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (formData.patient_id && !formData.client_name) {
      dispatch(fetchPatient(formData.patient_id))
        .unwrap()
        .then((p) => {
          setFormData((prev) => ({ ...prev, client_name: `${p.first_name || ""} ${p.last_name || ""}`.trim() }));
        })
        .catch((err) => console.error("Failed to prefill patient:", err));
    }
  }, [dispatch, formData.patient_id, formData.client_name, setFormData]);

  const handleChange = (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const setRating = (key, val) => {
    if (readOnly) return;
    setFormData((prev) => ({ ...prev, [key]: val }));
  };

  return (
    <div className="p-3" style={{ maxHeight: "70vh", overflowY: "auto", scrollbarWidth: "thin" }}>
      {/* Likert legend */}
      <div className="alert alert-light border mb-3">
        <div className="fw-bold mb-1">Likert Scale Guide</div>
        <div className="row g-1 small">
          {FAMILY_LIKERT_LEGEND.map((l) => (
            <div className="col-md-12" key={l.n}><strong>{l.n} = {l.label}</strong></div>
          ))}
        </div>
      </div>

      <div className="accordion" id="familyFeedbackAccordion">
        {/* Section A: Basic Information */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold" type="button" data-bs-toggle="collapse" data-bs-target="#ffBasic">
              Section A: Basic Information
            </button>
          </h2>
          <div id="ffBasic" className="accordion-collapse collapse show">
            <div className="accordion-body pt-3">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="client_name" value={formData.client_name || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Client Name</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating form-floating-outline">
                    <input type="date" className="form-control" name="date_of_discharge" value={formData.date_of_discharge || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Date of Discharge</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="interviewer_name" value={formData.interviewer_name || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Interviewer Name</label>
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-floating form-floating-outline">
                    <textarea className="form-control" style={{ height: "90px" }} name="comments" value={formData.comments || ""} onChange={handleChange} disabled={readOnly}></textarea>
                    <label>Comments</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Likert sections with per-question comment */}
        {FAMILY_SECTIONS.map((section, sIdx) => (
          <div className="accordion-item mb-3 shadow-sm rounded" key={section.title}>
            <h2 className="accordion-header">
              <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target={`#ffSec${sIdx}`}>
                {section.title}
              </button>
            </h2>
            <div id={`ffSec${sIdx}`} className="accordion-collapse collapse">
              <div className="accordion-body pt-3">
                {section.questions.map((q) => (
                  <div key={q.key} className="border rounded-3 p-3 mb-3 bg-light-subtle">
                    <div className="fw-semibold mb-2">{q.label}</div>
                    <div className="d-flex flex-wrap justify-content-center gap-3 mb-2">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <div className="form-check form-check-inline" key={n}>
                          <input type="radio" className="form-check-input" id={`${q.key}_${n}`} name={q.key} checked={Number(formData[q.key]) === n} onChange={() => setRating(q.key, n)} disabled={readOnly} />
                          <label className="form-check-label" htmlFor={`${q.key}_${n}`}>{n}</label>
                        </div>
                      ))}
                    </div>
                    <input type="text" className="form-control form-control-sm" placeholder="Comment" name={`${q.key}_comment`} value={formData[`${q.key}_comment`] || ""} onChange={handleChange} disabled={readOnly} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {/* Final Comments */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#ffFinal">
              Final Comments
            </button>
          </h2>
          <div id="ffFinal" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <textarea className="form-control" rows="5" name="final_comments" value={formData.final_comments || ""} onChange={handleChange} disabled={readOnly}></textarea>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamilyFeedbackForm;
